import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, Button, Badge, Drawer, Notice, AsyncBoundary, useToast,
} from '@kollez/design-system';
import { useAuthStore } from '@/lib/authStore';
import { useInterview, useRequestReschedule, useSubmitInterviewFeedback, useMarkNotifReadBySubject } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';
import { isOnlineInterviewMode, prettyRoundType } from '@kollez/shared/enums';

/**
 * /interviews/:id (F76) — layout mirrors kollez/pages/student/interview.html.
 *
 * Deep-audit fixes (Page 17/N):
 *   - 'Add to calendar' fired a toast lying that it had added the event.
 *     Now generates a real .ics download from the interview's fields so
 *     the student can import into Google/Apple/Outlook.
 *   - 'Directions' button linked to a #where anchor — a fake affordance
 *     that just scrolled the page. Removed; for online rounds we keep
 *     the real Join link when meetingLink exists, for offline rounds we
 *     surface the room name in the hero and no button pretends to route.
 *   - '~40 min' rendered even when duration was absent — invented data.
 *     Now renders 'duration to be confirmed' when Interview.duration is
 *     missing, real minutes otherwise.
 */

// ICS helpers live in @/lib/ics.ts (Pass 18) so student Home shares them.
import { buildIcsFile, downloadIcs } from '@/lib/ics';

export function InterviewDetailPage() {
  const { id = '' } = useParams();
  const { user } = useAuthStore();
  const iv = useInterview(id);
  const reschedule = useRequestReschedule(id);
  const feedback = useSubmitInterviewFeedback(id);
  const toast = useToast();

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Step 56 — landing on this page counts as "reading" any pending interview notifications
  // (scheduled / rescheduled — Step 55's writers). Fires once per (id, mount). Guarded via a
  // ref so React StrictMode's dev double-invoke can't double-fire. The server mutation is
  // caller-scoped, so a panel member opening the same URL harmlessly no-ops (they have no
  // matching unread rows). Non-fatal — refetch cycle catches any failure.
  const markRead = useMarkNotifReadBySubject();
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!id || firedFor.current === id) return;
    firedFor.current = id;
    void markRead.mutateAsync({ kind: 'Interview', id }).catch(() => {
      // Non-fatal — refetch cycle catches the discrepancy.
    });
  }, [id, markRead]);

  const isPanel = user?.permissions?.includes('interview:conduct') || user?.role === 'technical_panel';
  const isStudent = user?.role === 'student' || user?.role === 'student_representative';
  const interview = iv.data?.interview;

  const dayObj = interview?.scheduledAt ? new Date(interview.scheduledAt) : null;

  return (
    <AsyncBoundary
      kind="detail"
      loading={iv.isLoading}
      error={iv.error}
      empty={!iv.isLoading && !interview}
      emptyTitle="Interview not found"
      emptyHint="The link may be stale. Check your Interviews list for what's coming up."
      onRetry={() => void iv.refetch()}
    >
      {interview && dayObj && (
        <div className="stack">
          {/* Hero */}
          <Card padding="lg" className="bg-bg-inkwell text-white">
            <div className="flex gap-4 items-center">
              <div className="min-w-[76px] text-center rounded overflow-hidden bg-white text-ink-inkwell">
                <div className="text-[.7rem] font-bold uppercase tracking-wider bg-ink-inkwell text-white py-1">
                  {dayObj.toLocaleString('en-IN', { month: 'short' })}
                </div>
                <div className="font-serif text-4xl leading-tight">{dayObj.getDate()}</div>
                <div className="text-xs pb-1">{dayObj.toLocaleString('en-IN', { weekday: 'long' })}</div>
              </div>
              <div className="min-w-0">
                <p className="m-0 text-sm opacity-80">
                  {interview.partner?.name ?? 'Company'} · Round {interview.roundNumber} · {interview.roundType ? prettyRoundType(interview.roundType) : 'Interview'}
                </p>
                <h1 className="font-serif text-[1.625rem] leading-tight m-0">
                  {interview.roundType ? `${prettyRoundType(interview.roundType)} interview` : 'Interview'}
                </h1>
                <p className="m-0 mt-1 text-sm opacity-90">
                  {formatTime(interview.scheduledAt)} · {typeof interview.duration === 'number' ? `about ${interview.duration} minutes` : 'duration to be confirmed'}
                  {interview.room ? ` · ${interview.room}` : ''}
                </p>
              </div>
            </div>
            {interview.rescheduleReason && (
              <Notice tone="warning" className="mt-3">
                <b>Rescheduled.</b> Reason: {interview.rescheduleReason}
              </Notice>
            )}
          </Card>

          {/* Action row */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                downloadIcs(`interview-${dayObj.toISOString().slice(0, 10)}.ics`, buildIcsFile(interview));
                toast.show({ message: 'Downloaded .ics — open it to add the event to your calendar.' });
              }}
            >
              Add to calendar
            </Button>
            {interview.meetingLink && (
              <a href={interview.meetingLink} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary">Join link</Button>
              </a>
            )}
            {isStudent && interview.status === 'scheduled' && (
              <Button variant="secondary" onClick={() => setRescheduleOpen(true)}>Can't make it?</Button>
            )}
            {isPanel && !interview.feedback?.result && interview.status !== 'completed' && (
              <Button variant="primary" onClick={() => setFeedbackOpen(true)}>Submit panel feedback</Button>
            )}
          </div>

          {/* Bring-with-you — only when the round is in-person (Video / Online_Test hide it). */}
          {isStudent && !isOnlineInterviewMode(interview.mode) && (
            <BringChecklist />
          )}

          {/* Timeline */}
          <Card padding="lg" id="where">
            <h2 className="h-section mb-3">What to expect</h2>
            <ol className="grid gap-3 pl-4 border-l-2 border-line-primary">
              <li>
                <div className="font-medium">Report at the placement cell</div>
                <div className="text-xs text-ink-tertiary">By 30 min before slot · bring your ID</div>
              </li>
              <li>
                <div className="font-medium">{interview.roundType ? prettyRoundType(interview.roundType) : 'Round'} interview</div>
                <div className="text-xs text-ink-tertiary">
                  {typeof interview.duration === 'number' ? `~${interview.duration} min` : 'duration TBC'}
                  {typeof interview.interviewers?.length === 'number' && interview.interviewers.length > 0
                    ? ` · ${interview.interviewers.length} interviewer${interview.interviewers.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              </li>
              <li>
                <div className="font-medium">Results</div>
                <div className="text-xs text-ink-tertiary">On Kollez + WhatsApp when the panel submits feedback</div>
              </li>
            </ol>
          </Card>

          {/* Feedback (if already submitted, show it) */}
          {interview.feedback?.result && (
            <Card padding="lg">
              <h2 className="h-section mb-2">Panel feedback</h2>
              <div className="flex items-baseline gap-3 flex-wrap">
                <Badge tone={
                  interview.feedback.result === 'Selected' ? 'success'
                  : interview.feedback.result === 'Rejected' ? 'warning'
                  : 'info'
                }>{interview.feedback.result.replace('_', ' ')}</Badge>
                {typeof interview.feedback.rating === 'number' && <span className="font-mono">{interview.feedback.rating}/5</span>}
                {interview.feedback.recommendation && <Badge tone="outline">{interview.feedback.recommendation.replace(/_/g, ' ')}</Badge>}
              </div>
              {interview.feedback.remarks && <p className="mt-2 whitespace-pre-line text-sm">{interview.feedback.remarks}</p>}
            </Card>
          )}

          {/* Reschedule drawer */}
          <RescheduleDrawer
            open={rescheduleOpen}
            onClose={() => setRescheduleOpen(false)}
            onSubmit={async (body) => {
              try {
                await reschedule.mutateAsync(body);
                toast.show({ message: 'Request sent. Keep your current slot until it\'s approved.' });
                setRescheduleOpen(false);
              } catch (err) {
                const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Could not send request.';
                toast.show({ tone: 'warning', message: msg });
              }
            }}
            pending={reschedule.isPending}
          />

          {/* Panel feedback drawer */}
          <FeedbackDrawer
            open={feedbackOpen}
            onClose={() => setFeedbackOpen(false)}
            onSubmit={async (body) => {
              try {
                await feedback.mutateAsync(body);
                toast.show({ message: 'Feedback saved. Result relayed to the placement cell.' });
                setFeedbackOpen(false);
              } catch {
                toast.show({ tone: 'warning', message: 'Could not submit feedback.' });
              }
            }}
            pending={feedback.isPending}
          />
        </div>
      )}
    </AsyncBoundary>
  );
}

function BringChecklist() {
  const items = [
    ['College ID card', 'Security checks it at the gate'],
    ['2 copies of resume', 'The version you applied with'],
    ['Semester grade sheets', 'Originals + one photocopy set'],
    ['Formal clothes', 'Shirt and trousers or salwar is fine'],
  ];
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const count = Object.values(checked).filter(Boolean).length;
  return (
    <Card padding="lg">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="h-section">Bring with you</h2>
        <span className="text-xs text-ink-tertiary">{count} of {items.length} ready</span>
      </div>
      <div className="grid divide-y divide-line-tertiary">
        {items.map(([title, sub], i) => (
          <label key={i} className="flex gap-3 items-start py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(checked[i])}
              onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))}
              className="mt-1"
            />
            <div>
              <div className={`font-medium ${checked[i] ? 'line-through text-ink-tertiary' : ''}`}>{title}</div>
              <div className="text-xs text-ink-tertiary">{sub}</div>
            </div>
          </label>
        ))}
      </div>
    </Card>
  );
}

function RescheduleDrawer({
  open, onClose, onSubmit, pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: { reason: 'exam_clash' | 'other_interview' | 'health_family' | 'other'; note?: string }) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState<'exam_clash' | 'other_interview' | 'health_family' | 'other'>('other');
  const [note, setNote] = useState('');
  const options: Array<[typeof reason, string]> = [
    ['other_interview', 'Another interview at the same time'],
    ['exam_clash', 'University exam'],
    ['health_family', 'Health or family emergency'],
    ['other', 'Something else'],
  ];
  return (
    <Drawer open={open} onClose={onClose} title="Ask for a different time" size="md">
      <div className="stack">
        <Notice tone="warning">
          Requests close 12 hours before the slot. Changes aren't guaranteed.
        </Notice>
        <div className="grid gap-2">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Reason</span>
          {options.map(([val, label]) => (
            <label key={val} className="flex gap-2 items-center text-sm cursor-pointer">
              <input
                type="radio"
                name="rr"
                checked={reason === val}
                onChange={() => setReason(val)}
              />
              {label}
            </label>
          ))}
        </div>
        <label className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Anything the TPO should know? (optional)</span>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="input" />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Keep my slot</Button>
          <Button variant="primary" onClick={() => onSubmit({ reason, note: note || undefined })} loading={pending}>Send request</Button>
        </div>
      </div>
    </Drawer>
  );
}

function FeedbackDrawer({
  open, onClose, onSubmit, pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    rating: number;
    strengths?: string[];
    weaknesses?: string[];
    recommendation: 'Strongly_Recommend' | 'Recommend' | 'Neutral' | 'Not_Recommend' | 'Strongly_Not_Recommend';
    remarks?: string;
    result: 'Selected' | 'Rejected' | 'On_Hold' | 'Needs_Another_Round' | 'Pending';
  }) => void;
  pending: boolean;
}) {
  const [rating, setRating] = useState(3);
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [recommendation, setRecommendation] = useState<Parameters<typeof onSubmit>[0]['recommendation']>('Neutral');
  const [result, setResult] = useState<Parameters<typeof onSubmit>[0]['result']>('Pending');
  const [remarks, setRemarks] = useState('');

  return (
    <Drawer open={open} onClose={onClose} title="Panel feedback" size="lg">
      <div className="stack">
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Overall rating (1-5)</span>
            <input
              type="number" min={1} max={5} value={rating}
              onChange={(e) => setRating(Math.max(1, Math.min(5, Number(e.target.value))))}
              className="input"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Recommendation</span>
            <select value={recommendation} onChange={(e) => setRecommendation(e.target.value as typeof recommendation)} className="input">
              <option value="Strongly_Recommend">Strongly recommend</option>
              <option value="Recommend">Recommend</option>
              <option value="Neutral">Neutral</option>
              <option value="Not_Recommend">Not recommend</option>
              <option value="Strongly_Not_Recommend">Strongly not recommend</option>
            </select>
          </label>
        </div>
        <label className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Result</span>
          <select value={result} onChange={(e) => setResult(e.target.value as typeof result)} className="input">
            <option value="Pending">Pending</option>
            <option value="Selected">Selected</option>
            <option value="Rejected">Rejected</option>
            <option value="On_Hold">On hold</option>
            <option value="Needs_Another_Round">Needs another round</option>
          </select>
        </label>
        <label className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Strengths (one per line)</span>
          <textarea rows={3} value={strengths} onChange={(e) => setStrengths(e.target.value)} className="input" placeholder="Clear communication&#10;Strong on SQL fundamentals" />
        </label>
        <label className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Weaknesses / gaps (one per line)</span>
          <textarea rows={3} value={weaknesses} onChange={(e) => setWeaknesses(e.target.value)} className="input" />
        </label>
        <label className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-ink-tertiary">Remarks</span>
          <textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input" />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={pending}
            onClick={() => onSubmit({
              rating,
              strengths: strengths.split('\n').map((s) => s.trim()).filter(Boolean),
              weaknesses: weaknesses.split('\n').map((s) => s.trim()).filter(Boolean),
              recommendation,
              result,
              remarks: remarks || undefined,
            })}
          >
            Save feedback
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
